import { OutboundReferenceCollection, ProductInventoryUnit, ProductSupplierDoc } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, RowClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Checkbox, Input, Popconfirm, Switch, notification } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { sumBy } from 'lodash-es';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { combineLatest } from 'rxjs';
import { purchaseBundleToKr } from 'src/constants';
import { mutate } from 'swr';

import {
  formatNumberForAgCell,
  inventoryQuantityFormatter,
  onCellValueChangedWithUpdate,
  uomUnitCellStyle,
} from 'src/utils/ag-grid-util';
import { AuditLogger } from 'src/utils/audit-logger';
import { errorObjectToString } from 'src/utils/common';
import { dayjsesToFormatedDate, getDefaultDates } from 'src/utils/date-util';
import {
  getProductOutboundTransactions,
  observeInventoryBinLocation,
  observeProductInventories,
  observeProductSupplier,
  updateProductInventory,
  updateProductInventorySupplierId,
} from 'src/utils/firebase/firebase-short-cut';
import {
  InventoryTableHandlers,
  InventoryTableRowData,
  isEditableBarcodeCell,
  isEditableCostCell,
  mergeProductInventoryWithBinLocation,
  purchaseBundleValueFormatter,
} from 'src/utils/product-inventory-util';
import { getPurchasePreviewMessage } from 'src/utils/supplier-purchase-order-util';

import { useTitle } from 'src/hooks/useTitle';

import AgCellDepletionDateOrderQty from 'src/atomic-components/atoms/AgCell/AgCellDepletionDateOrderQty';
import AgCellDepletionDateValue from 'src/atomic-components/atoms/AgCell/AgCellDepletionDateValue';
import AgCellInventoryDepletionDate from 'src/atomic-components/atoms/AgCell/AgCellInventoryDepletionDate';
import AgCellInventoryIssueEditor from 'src/atomic-components/atoms/AgCell/AgCellInventoryIssueEditor';
import AgCellInventoryTooltip from 'src/atomic-components/atoms/AgCell/AgCellInventoryTooltip';
import AgCellProductSupplierSelect from 'src/atomic-components/atoms/AgCell/AgCellProductSupplierSelect';
import AgCellToggleEditor from 'src/atomic-components/atoms/AgCell/AgCellToggleEditor';

const productSupplierPath = 'productSupplier';
const onCellValueChanged = onCellValueChangedWithUpdate<InventoryTableRowData>('재고 정보');

interface InventoryTableProps {
  rowData: InventoryTableRowData[];
  productSuppliers: ProductSupplierDoc[];
}

const getRowStyle = (params: RowClassParams<InventoryTableRowData>) => {
  return {
    backgroundColor: params.data?.productSupplier?.groupId === 'E' ? 'var(--blue50)' : 'white',
  };
};

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

interface OutboundComputedData {
  depletionDate: number;
  orderQty: number;
}

const InventoryTable: FC<InventoryTableProps> = ({ rowData, productSuppliers }) => {
  const [editMode, setEditMode] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [exporting, setExporting] = useState(false);
  const gridRef = useRef<AgGridReact<InventoryTableRowData>>(null);
  const outboundDataMapRef = useRef<Map<string, OutboundComputedData>>(new Map());

  const handleExcelExport = async () => {
    const api = gridRef.current?.api;
    if (!api) return;

    setExporting(true);
    try {
      const [startDate, endDate] = getDefaultDates(30, 1);
      const [startValue, endValue] = dayjsesToFormatedDate(startDate, endDate, 'yyyy-MM-dd');
      const map = new Map<string, OutboundComputedData>();
      let totalReadCount = 0;

      // 현재 그리드에 표시된 모든 행의 outbound 데이터를 가져온다.
      const rows: InventoryTableRowData[] = [];
      api.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) rows.push(node.data);
      });

      await Promise.all(
        rows
          .filter((row) => isFinite(row.quantity))
          .map(async (row) => {
            try {
              const txns = await getProductOutboundTransactions([['inventoryId', '==', row._id]], {
                sortKey: 'requestDate',
                orderBy: 'desc',
                startValue,
                endValue,
              });
              totalReadCount += txns.length;
              // SWR 캐시에도 저장하여 셀 렌더러가 마운트될 때 중복 fetch를 방지한다.
              mutate(`productOutboundTransactions-${row._id}`, txns, false);
              const orderTxns = txns.filter((t) => t.referenceCollection === OutboundReferenceCollection.ORDER);
              const outboundSum = sumBy(orderTxns, 'requestQuantity');
              const quantity = row.quantity ?? 0;
              const estimatedQty = outboundSum !== 0 ? quantity / (outboundSum / 30) : 0;
              map.set(row._id, {
                depletionDate: orderTxns.length === 0 ? 0 : estimatedQty,
                orderQty: orderTxns.length === 0 ? 0 : outboundSum,
              });
            } catch {
              // skip
            }
          })
      );

      outboundDataMapRef.current = map;

      // 그룹 상태와 무관하게 개별 컬럼(소진일, 30일 주문량)을 항상 포함하고, 합산 컬럼은 제외한다.
      const columnApi = gridRef.current?.columnApi;
      const exportColumnKeys = (columnApi?.getAllGridColumns() ?? [])
        .filter((col) => {
          const colId = col.getColId();
          return colId !== '_id' && colId !== 'depletionDateCombined';
        })
        .map((col) => col.getColId());
      api.exportDataAsExcel({ columnKeys: exportColumnKeys });

      const fetchCount = rows.filter((row) => isFinite(row.quantity)).length;
      const auditLogger = AuditLogger.getInstance();
      auditLogger.log({
        action: 'SYNC',
        entity: 'inventory',
        entityId: 'excel-export',
        summary: `[재고 정보] 엑셀 다운로드 완료 (문서 ${totalReadCount}건 조회 / 재고 ${fetchCount}건)`,
        metadata: { totalReadCount, fetchCount, totalRows: rows.length },
        slack: { channel: '4-자동화' },
      });
    } catch {
      notification.error({ message: '엑셀 다운로드에 실패했습니다.' });
    } finally {
      setExporting(false);
    }
  };

  const onChangeShowInactive = (e: CheckboxChangeEvent) => {
    setShowInactive(e.target.checked);
  };

  const columnDefs: (ColDef<InventoryTableRowData> | ColGroupDef<InventoryTableRowData>)[] = useMemo(
    () => [
      {
        headerName: 'ID',
        field: '_id',
        width: 100,
        hide: true,
      },
      {
        headerName: '*구분',
        field: 'productSupplier.groupId',
        width: 88,
        editable: editMode,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data.productSupplier?._id, 'groupId', true, false, ''),
      },
      {
        headerName: '*거래처',
        field: 'productSupplier.name',
        width: 140,
        editable: editMode,
        cellRenderer: (params: any) => params.value,
        cellEditor: (params: any) => (
          <AgCellProductSupplierSelect
            {...params}
            productSuppliers={params.context?.productSuppliers ?? []}
            onUpdate={async (supplierId) => {
              try {
                await updateProductInventorySupplierId(params.data._id, supplierId);
              } catch (error) {
                const description = errorObjectToString(error);
                notification.error({
                  message: '거래처 변경에 실패했습니다.',
                  description,
                });
              }
            }}
          />
        ),
      },
      {
        headerName: '*재고명',
        field: 'inventoryName',
        width: 300,
        editable: editMode,
        valueSetter: InventoryTableHandlers.inventoryName.valueSetter,
        onCellValueChanged: InventoryTableHandlers.inventoryName.onCellValueChanged,
        cellRenderer: AgCellInventoryTooltip,
      },
      {
        headerName: '재고량',
        field: 'quantity',
        width: 96,
        valueFormatter: inventoryQuantityFormatter,
        editable: editMode,
        cellEditorPopup: true,
        cellEditor: (params: any) => <AgCellInventoryIssueEditor {...params} inventoryData={params.data} />,
      },
      {
        headerName: '예상 소진일',
        headerTooltip: '예상 소진일(30일간 주문량)',
        marryChildren: true,
        children: [
          {
            colId: 'depletionDateCombined',
            headerName: '예상 소진일',
            field: 'depletionDate',
            width: 120,
            filter: false,
            columnGroupShow: 'closed',
            valueGetter: (params) => {
              const data = params.data as any;
              if (data?.depletionDate != null) return data.depletionDate;
              return outboundDataMapRef.current.get(data?._id)?.depletionDate ?? null;
            },
            cellRenderer: AgCellInventoryDepletionDate,
            cellRendererParams: (params: any) => ({
              inventoryId: params.data?._id,
              quantity: params.data?.quantity,
            }),
          },
          {
            colId: 'depletionDateValue',
            headerName: '소진일',
            field: 'depletionDate',
            width: 100,
            filter: false,
            columnGroupShow: 'open',
            valueGetter: (params) => {
              const data = params.data as any;
              if (data?.depletionDate != null) return data.depletionDate;
              return outboundDataMapRef.current.get(data?._id)?.depletionDate ?? null;
            },
            cellRenderer: AgCellDepletionDateValue,
            cellRendererParams: (params: any) => ({
              inventoryId: params.data?._id,
              quantity: params.data?.quantity,
            }),
          },
          {
            headerName: '30일 주문량',
            field: 'orderQuantity30d',
            width: 110,
            filter: false,
            columnGroupShow: 'open',
            valueGetter: (params) => {
              const data = params.data as any;
              if (data?.orderQuantity30d != null) return data.orderQuantity30d;
              return outboundDataMapRef.current.get(data?._id)?.orderQty ?? null;
            },
            cellRenderer: AgCellDepletionDateOrderQty,
            cellRendererParams: (params: any) => ({
              inventoryId: params.data?._id,
              quantity: params.data?.quantity,
            }),
          },
        ],
      },
      {
        headerName: '*적정 재고량',
        field: 'optimalStockMemo',
        width: 120,
        editable: editMode,
        valueSetter: InventoryTableHandlers.optimalStockMemo.valueSetter,
        onCellValueChanged: InventoryTableHandlers.optimalStockMemo.onCellValueChanged,
      },
      {
        headerName: '*재고 위치',
        field: 'binLocation',
        width: 110,
        editable: editMode,
        valueSetter: InventoryTableHandlers.binLocation.valueSetter,
        onCellValueChanged: InventoryTableHandlers.binLocation.onCellValueChanged,
      },
      {
        headerName: '매입가',
        headerClass: 'bgGreen200',
        children: [
          {
            headerName: '*ea',
            field: 'uom.ea.cost',
            width: 100,
            headerClass: 'bgGreen200',
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: uomUnitCellStyle,
            editable: (params) => editMode && isEditableCostCell(params),
            valueSetter: InventoryTableHandlers.cost.valueSetter,
            onCellValueChanged: InventoryTableHandlers.cost.onCellValueChanged,
          },
          {
            headerName: '*bundle',
            field: 'uom.bundle.cost',
            width: 110,
            headerClass: 'bgGreen200',
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: uomUnitCellStyle,
            editable: (params) => editMode && isEditableCostCell(params),
            valueSetter: InventoryTableHandlers.cost.valueSetter,
            onCellValueChanged: InventoryTableHandlers.cost.onCellValueChanged,
          },
        ],
      },
      {
        headerName: '발주 설정',
        headerClass: 'bgBlue200',
        children: [
          {
            headerName: '*발주명',
            field: 'purchaseConfig.purchaseProductName',
            width: 220,
            headerClass: 'bgBlue200',
            editable: editMode,
            valueSetter: InventoryTableHandlers.purchaseConfig.valueSetter,
            onCellValueChanged: InventoryTableHandlers.purchaseConfig.onCellValueChanged,
            tooltipValueGetter: (params) =>
              params.data?.purchaseConfig ? getPurchasePreviewMessage(params.data.purchaseConfig) : '',
          },
          {
            headerName: '*묶음방식',
            field: 'purchaseConfig.purchaseBundle',
            width: 140,
            headerClass: 'bgBlue200',
            valueFormatter: purchaseBundleValueFormatter,
            filterParams: {
              valueFormatter: purchaseBundleValueFormatter,
            },
            editable: editMode,
            cellEditorPopup: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: Object.keys(purchaseBundleToKr),
              cellRenderer: ({ value }: any) => <span>{purchaseBundleToKr[value] ?? ''}</span>,
            },
            cellRenderer: ({ value }: any) => <span>{purchaseBundleToKr[value] ?? ''}</span>,
            valueSetter: InventoryTableHandlers.purchaseConfig.valueSetter,
            onCellValueChanged: InventoryTableHandlers.purchaseConfig.onCellValueChanged,
          },
          {
            headerName: '*최대묶음',
            field: 'purchaseConfig.purchaseMaxBundle',
            width: 120,
            headerClass: 'bgBlue200',
            editable: editMode,
            valueSetter: InventoryTableHandlers.purchaseConfig.valueSetter,
            onCellValueChanged: InventoryTableHandlers.purchaseConfig.onCellValueChanged,
          },
          {
            headerName: '*단위 변환 값',
            field: 'purchaseConfig.purchaseMultiplyUnit',
            width: 140,
            headerClass: 'bgBlue200',
            editable: editMode,
            valueSetter: InventoryTableHandlers.purchaseConfig.valueSetter,
            onCellValueChanged: InventoryTableHandlers.purchaseConfig.onCellValueChanged,
          },
          {
            headerName: '*단위',
            field: 'purchaseConfig.purchaseUnit',
            width: 120,
            headerClass: 'bgBlue200',
            editable: editMode,
            valueSetter: InventoryTableHandlers.purchaseConfig.valueSetter,
            onCellValueChanged: InventoryTableHandlers.purchaseConfig.onCellValueChanged,
          },
        ],
      },
      {
        headerName: '바코드',
        headerClass: 'bgYellow200',
        children: [
          {
            headerName: '*ea',
            field: 'uom.ea.barcode',
            width: 148,
            headerClass: 'bgYellow200',
            cellStyle: uomUnitCellStyle,
            editable: (params) => editMode && isEditableBarcodeCell(params),
            valueSetter: InventoryTableHandlers.barcode.valueSetter,
            onCellValueChanged: InventoryTableHandlers.barcode.onCellValueChanged(ProductInventoryUnit.EA),
          },
          {
            headerName: '*bundle',
            field: 'uom.bundle.barcode',
            width: 148,
            headerClass: 'bgYellow200',
            cellStyle: uomUnitCellStyle,
            editable: (params) => editMode && isEditableBarcodeCell(params),
            valueSetter: InventoryTableHandlers.barcode.valueSetter,
            onCellValueChanged: InventoryTableHandlers.barcode.onCellValueChanged(ProductInventoryUnit.BUNDLE),
          },
        ],
      },
      {
        headerName: '*사용',
        field: 'active',
        headerTooltip: '미사용 처리시 재고를 사용하는 상품 모두 판매중지 처리됩니다.\n(사용은 관계없음)',
        width: 110,
        filterParams: {
          valueFormatter: (params: any) => (params.value ? '사용' : '미사용'),
        },
        cellRenderer: (params: any) => (
          <AgCellToggleEditor
            id={params.data._id}
            value={params.data.active}
            field='active'
            size='small'
            disabled={!editMode}
            onChange={updateProductInventory}
          />
        ),
      },
    ],
    [editMode]
  );

  return (
    <div className='height100 flexColumn'>
      <div className='flexRow flexSpaceBetween'>
        <div className='flexRow flexAlignCenter'>
          <div className='flexRow flexAlignCenter'>
            <div>편집 모드</div>
            <Switch
              checked={editMode}
              onChange={(checked) => setEditMode(checked)}
              checkedChildren='ON'
              unCheckedChildren='OFF'
            />
          </div>
          <div className='flexRow flexAlignCenter'>
            <div style={{ borderLeft: '1px solid var(--gray200)', height: 24 }} />
            <Input
              placeholder='전체 검색...'
              onChange={(e) => setQuickFilterText(e.target.value)}
              style={{ width: 240 }}
            />
          </div>
        </div>
        <div className='flexRow flexAlignCenter'>
          <Checkbox checked={showInactive} onChange={onChangeShowInactive}>
            미사용 재고 표시
          </Checkbox>
          <Popconfirm
            title='엑셀 다운로드'
            description='예상 소진일 데이터를 조회하기 위해 많은 리소스가 사용됩니다. 계속하시겠습니까?'
            onConfirm={handleExcelExport}
            okText='다운로드'
            cancelText='취소'>
            <Button loading={exporting}>엑셀 다운로드</Button>
          </Popconfirm>
        </div>
      </div>
      <div className='ag-theme-alpine height100 flex1'>
        <AgGridReact
          ref={gridRef}
          rowData={showInactive ? rowData : rowData.filter((row) => row.active)}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data._id}
          context={{ productSuppliers }}
          // 편집 완료후 스크롤 이동을 막는다.
          suppressScrollOnNewData={true}
          getRowStyle={getRowStyle}
          tooltipShowDelay={500}
          quickFilterText={quickFilterText}
        />
      </div>
    </div>
  );
};

const InventoryBoard: FC = () => {
  useTitle('재고 정보');
  const [rowData, setRowData] = useState<InventoryTableRowData[]>([]);
  const [productSuppliers, setProductSuppliers] = useState<ProductSupplierDoc[]>([]);
  useEffect(() => {
    const productInventoryObservable = observeProductInventories([['deletedAt', '==', null]]);
    const productSupplierObservable = observeProductSupplier();
    const inventoryBinLocationObservable = observeInventoryBinLocation();

    const subscription = combineLatest([
      productInventoryObservable,
      productSupplierObservable,
      inventoryBinLocationObservable,
    ]).subscribe(([productInventories, productSuppliers, config]) => {
      const rowData = mergeProductInventoryWithBinLocation(productInventories, productSuppliers, config);
      setRowData(rowData);
      setProductSuppliers(productSuppliers);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <div className='flexColumn height100'>
      <InventoryTable rowData={rowData} productSuppliers={productSuppliers} />
    </div>
  );
};

export default InventoryBoard;
